// FamilyTreeDashboard.jsx
import React, { useState, useEffect, useContext } from "react";
import { Users, Plus, Edit2 } from "lucide-react"; // Added Edit2 for pencil
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import CreateFamilyTree from "./CreateFamilyTree";
import axios from "axios";
import { AuthContext } from "react-oauth2-code-pkce";

export default function FamilyTreeDashboard() {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [showPopup, setShowPopup] = useState(false);
  const [toast, setToast] = useState("");
  const [familyTrees, setFamilyTrees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [members, setMembers] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [editingFamilyId, setEditingFamilyId] = useState(null); // <-- track editing
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = window.innerWidth >= 1024 ? 9 : 6;
  const [selectedEvent, setSelectedEvent] = useState(null);

  // --- INVITATIONS STATE (new) ---
  const [invitations, setInvitations] = useState([]);
  const [loadingInvitations, setLoadingInvitations] = useState(false);
  const [invitationsPage, setInvitationsPage] = useState(1);
  const invitationsPerPage = 5; // pagination
  const [invitationProcessingId, setInvitationProcessingId] = useState(null);

  const API_BASE =
    process.env.REACT_APP_API_BASE_URL || "https://familyduniya-dev.censeotech.com/api-family";

  // -------------------------
  // Helper: decode JWT safely to read email (if needed)
  // -------------------------
  const parseJwt = (jwt) => {
    if (!jwt) return null;
    try {
      const base64Url = jwt.split(".")[1];
      if (!base64Url) return null;
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map(function (c) {
            return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
          })
          .join("")
      );
      return JSON.parse(jsonPayload);
    } catch (e) {
      console.warn("parseJwt failed", e);
      return null;
    }
  };
// Convert LocalizedDate into a readable date string
const formatDateTimeFromLocalized = (localizedDate) => {
  try {
    const str = localizedDate?.value ?? localizedDate?.date ?? localizedDate;
    if (!str) return "";
    const dt = new Date(str);
    if (isNaN(dt.getTime())) return String(str);
    return dt.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch (e) {
    return "";
  }
};

  // -------------------------
  // Fetch family trees (existing)
  // -------------------------
  const fetchFamilyTrees = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE}/family?page=1`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setFamilyTrees(res.data.data || []);
    } catch (error) {
      console.error("Error fetching family trees:", error);
      setFamilyTrees([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFamilyTrees();
  }, [token]);

  // -------------------------
  // Fetch invitations (new)
  // -------------------------
const fetchInvitations = async () => {
  setLoadingInvitations(true);

  try {
    // Extract email from JWT token
    const decoded = parseJwt(token);
    const emailFromToken =
      decoded?.email ||
      decoded?.preferred_username ||
      null;

    if (!emailFromToken) {
      console.warn("No email found in token -> invitations cannot be fetched by email.");
      setInvitations([]);
      return;
    }

    const res = await axios.get(
      `${API_BASE}/user/invitations/pending`,
      {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      }
    );

    // Normalize API response -> ALWAYS return array
    let raw = res.data;

    // Extract nested .data structures
    if (raw?.data) raw = raw.data;
    if (raw?.data) raw = raw.data;

    // Ensure array
    const finalList = Array.isArray(raw) ? raw : [];

    setInvitations(finalList);

  } catch (err) {
    console.error("Error fetching invitations:", err);
    setInvitations([]);
  } finally {
    setLoadingInvitations(false);
  }
};

useEffect(() => {
  if (token) {
    fetchInvitations();
  }
}, [token]);

useEffect(() => {
  const fetchEvents = async () => {
    if (!token) {
      setMembers([]);
      setLoadingEvents(false);
      return;
    }

    setLoadingEvents(true);

    try {
      const res = await axios.get(`${API_BASE}/user/events`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const events = res.data?.data || [];

      setMembers(events);  // ← direct response from API
      setEventPage(1);     // ← reset pagination to show item if single
    } catch (err) {
      console.error("Error fetching user events:", err);
      setMembers([]);
    } finally {
      setLoadingEvents(false);
    }
  };

  fetchEvents();
}, [token]);

  const formatDate = (dateObj) =>
    dateObj?.value
      ? new Date(dateObj.value).toLocaleDateString("en-US", { month: "short", day: "numeric" })
      : "";

  // -------------------------
  // Invitations pagination & UI helpers
  // -------------------------
  const totalInvitationPages = Math.ceil((invitations?.length || 0) / invitationsPerPage);
  const startInvitationIndex = (invitationsPage - 1) * invitationsPerPage;
  const paginatedInvitations = invitations.slice(startInvitationIndex, startInvitationIndex + invitationsPerPage);

  // -------------------------
  // Accept / Reject handlers (new)
  // -------------------------
  const patchInvitationStatus = async (invitationObj, newStatus) => {
    // invitationObj should be the full InvitationDto as requested.
    // We will PATCH the same object with updated status per your requirement.
    if (!invitationObj || !invitationObj.invitationId) {
      console.warn("Invalid invitation object for patch", invitationObj);
      return;
    }

    const payload = {
      ...invitationObj,
      status: newStatus,
    };

    try {
      setInvitationProcessingId(invitationObj.invitationId);
      const res = await axios.patch(`${API_BASE}/user/invitation`, payload, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      // After success, refresh invitations so UI stays consistent
      await fetchInvitations();
      await fetchFamilyTrees(); 
      // optional toast
      setToast(`Invitation ${newStatus === "ACCEPTED" ? "accepted" : "rejected"}.`);
      setTimeout(() => setToast(""), 3500);
    } catch (err) {
      console.error("Error updating invitation status:", err);
      setToast("Error updating invitation. Try again.");
      setTimeout(() => setToast(""), 3500);
    } finally {
      setInvitationProcessingId(null);
    }
  };

  const acceptInvitation = async (inv) => {
    await patchInvitationStatus(inv, "ACCEPTED");
  };

  const rejectInvitation = async (inv) => {
    await patchInvitationStatus(inv, "REJECTED");
  };

  // -------------------------
  // calculated existing values (existing logic preserved)
  // -------------------------
  const filteredTrees = familyTrees.filter(
    (tree) =>
      (tree?.name || "").toLowerCase().includes(search.toLowerCase()) ||
      (tree?.description || "").toLowerCase().includes(search.toLowerCase())
  );

  {/* Pagination setup */}
  const [eventPage, setEventPage] = useState(1);
  const eventsPerPage = 6;
  const totalEventPages = Math.ceil(members.length / eventsPerPage);
  const startEventIndex = (eventPage - 1) * eventsPerPage;

  const paginatedEvents = members.slice(startEventIndex, startEventIndex + eventsPerPage);


  const totalPages = Math.ceil(filteredTrees.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedTrees = filteredTrees.slice(startIndex, startIndex + itemsPerPage);


  return (
    <div className="p-4 md:p-6 space-y-6 bg-background min-h-screen">
      {/* Search */}
      <div className="flex flex-col md:flex-row justify-between items-center gap-4">
        <div className="relative w-full md:w-1/3">
          <input
            type="text"
            placeholder="Search for family..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full px-4 py-2 pr-10 border rounded-xl shadow-sm focus:outline-none"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">

        {/* ---------- LEFT: Family Tree List (kept intact) ---------- */}
        <div className="bg-white rounded-xl shadow p-4">
          <div className="flex items-center justify-between mb-4">
           <h2 className="text-2xl font-bold text-black">
               Family Tree List
             </h2>
            <button
              onClick={() => {
                setEditingFamilyId(null);
                setShowPopup(true);
              }}
              className="flex items-center gap-1 text-white bg-purple-600 hover:bg-purple-700 px-3 py-1 rounded-full text-sm"
                >
              <Plus size={16} /> Create a tree
            </button>
          </div>

          {loading ? (
            <div className="text-gray-500">Loading...</div>
          ) : filteredTrees.length === 0 ? (
            <div className="text-center text-gray-600 py-6">
              <p className="mb-4">
                <span className="font-semibold text-green-700">Family Duniya</span>! is a simple and secure way to preserve your family’s legacy.
              </p>
              <button
                onClick={() => {
                  setEditingFamilyId(null);
                  setShowPopup(true);
                }}
                className="flex items-center gap-2 mx-auto text-white bg-green-600 hover:bg-green-700 px-4 py-2 rounded-full text-sm"
              >
                <Plus size={16} /> Create Your First Tree
              </button>
            </div>
          ) : (
            <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
              {paginatedTrees.map((tree) => (
                <motion.div
                  key={tree.id}
                  whileHover={{ y: -6 }}
                  transition={{ duration: 0.3 }}
                  className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden relative cursor-pointer flex flex-col"
                >
                  {/* Edit Pencil */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingFamilyId(tree.id);
                      setShowPopup(true);
                    }}
                    className="absolute top-2 right-2 z-20 bg-white/70 backdrop-blur-sm hover:bg-white text-gray-800 hover:text-indigo-600 p-1.5 rounded-full shadow-sm transition-all duration-200"
                  >
                    <Edit2 size={16} />
                  </button>

                  {/* Image section - smaller height */}
                  <div
                    onClick={() => navigate(`/familyHome/${tree.id}`)}
                    className="relative h-36 overflow-hidden"
                  >
                    <img
                      src={tree.avatar || "/images/default-member.jpg"}
                      alt={tree.name}
                      className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
                  </div>

                  {/* Info section */}
                  <div className="p-3 flex flex-col justify-between flex-shrink-0">
                    <h3 className="text-base font-semibold text-gray-900 text-center">
                      {tree.name}
                    </h3>

                    <p className="text-gray-600 text-xs text-center mt-1 mb-2 line-clamp-1">
                      {tree.description || "Building connections across generations."}
                    </p>

                    <div className="flex items-center justify-between">
                      <span className="text-gray-600 text-xs font-medium">
                        {tree.members || 0} Members
                      </span>

                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (tree.members === 0) {
                            navigate(`/familyHome/${tree.id}`, {
                              state: { defaultTab: "tree", openRegisterMember: true },
                            });
                          } else {
                            navigate(`/familyHome/${tree.id}`);
                          }
                        }}
                        className="flex items-center justify-center w-8 h-8 rounded-full border border-indigo-600 text-indigo-600 hover:bg-indigo-600 hover:text-white shadow-sm hover:shadow-md transition-transform transform hover:scale-110"
                      >
                        <Plus size={16} />
                      </button>
                    </div>
                  </div>

                </motion.div>


              ))}

            </div>

          )}

          {/* Modern Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex justify-center items-center mt-6 gap-2">
              <button
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                disabled={currentPage === 1}
                className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition-all ${
                  currentPage === 1
                    ? "opacity-50 cursor-not-allowed border-gray-300 text-gray-400"
                    : "border-purple-500 text-purple-600 hover:bg-purple-600 hover:text-white"
                }`}
              >
                ←
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .slice(Math.max(0, currentPage - 2), Math.min(totalPages, currentPage + 1))
                .map((page) => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                      currentPage === page
                        ? "bg-purple-600 text-white shadow"
                        : "border border-purple-500 text-purple-600 hover:bg-purple-600 hover:text-white"
                    }`}
                  >
                    {page}
                  </button>
                ))}

              <button
                onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                disabled={currentPage === totalPages}
                className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition-all ${
                  currentPage === totalPages
                    ? "opacity-50 cursor-not-allowed border-gray-300 text-gray-400"
                    : "border-purple-500 text-purple-600 hover:bg-purple-600 hover:text-white"
                }`}
              >
                →
              </button>
            </div>
          )}
        </div>

        {/* ---------- RIGHT: Invitations (NEW) ---------- */}
        <div className="bg-white rounded-xl shadow p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Users className="text-indigo-600" />
              <h2 className="text-2xl font-bold text-black">Invitations</h2>
              {/* Badge */}
              <span className="ml-2 inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-semibold bg-indigo-600 text-white">
                {invitations.length || 0}
              </span>
            </div>

            <div className="text-sm text-gray-500">
              {loadingInvitations ? "Loading..." : `${invitations.length || 0} total`}
            </div>
          </div>

          {/* Invitations list */}
          {loadingInvitations ? (
            <div className="text-gray-500">Loading invitations...</div>
          ) : invitations.length === 0 ? (
            <div className="text-center text-gray-600 py-6">
              <p>No invitations found.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {paginatedInvitations.map((inv) => (
                <div
                  key={inv.invitationId}
                  className="flex items-start gap-3 p-3 border rounded-lg hover:shadow-sm transition"
                >
                  <img
                    src={inv.invitedToFamilyThumbnailFile || "/images/default-member.jpg"}
                    alt={inv.invitedToFamilyName}
                    className="w-16 h-16 object-cover rounded-md border"
                    onError={(e) => (e.currentTarget.src = "/images/default-member.jpg")}
                  />

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="text-sm font-semibold text-gray-900">
                          {inv.invitedToFamilyName || "Family"}
                        </div>
                        <div className="text-xs text-gray-500">
                          Invited by {inv.invitedByName || inv.invitedByEmail || "Someone"}
                        </div>
                      </div>

                      <div className="text-right text-xs text-gray-500">
                        <div>{formatDateTimeFromLocalized(inv.invitedDateTime) || ""}</div>
                        <div className="mt-2">
                          <span className="px-2 py-0.5 text-xs rounded bg-gray-100 text-gray-700">
                            {inv.status || "INVITED"}
                          </span>
                        </div>
                      </div>
                      
                    </div>
                     <div className="text-left text-xs text-gray-500">
                      <div className="mt-1 flex items-center gap-4">
                         <a
                        href="#"
                        onClick={async (e) => {
                          e.preventDefault();
                          if (invitationProcessingId) return;
                          await acceptInvitation(inv);
                        }}
                        className="text-indigo-600 hover:underline"
                        style={{ pointerEvents: invitationProcessingId ? "none" : "auto" }}
                      >
                        {invitationProcessingId === inv.invitationId ? "Processing..." : "Accept"}
                      </a>
                      <a
                        href="#"
                        onClick={async (e) => {
                          e.preventDefault();
                          if (invitationProcessingId) return;
                          await rejectInvitation(inv);
                        }}
                        className="text-red-600 hover:underline"
                        style={{ pointerEvents: invitationProcessingId ? "none" : "auto" }}
                      >
                        {invitationProcessingId === inv.invitationId ? "Processing..." : "Reject"}
                      </a>
                      </div>
                      </div>

                    <p className="text-xs text-gray-600 mt-2 line-clamp-2">
                      {inv.invitedToFamilyDetails || ""}
                    </p>
                  </div>
                </div>
              ))}

              {/* Invitations pagination controls */}
              {totalInvitationPages > 1 && (
                <div className="flex justify-center items-center gap-2 mt-3">
                  <button
                    onClick={() => setInvitationsPage((p) => Math.max(p - 1, 1))}
                    disabled={invitationsPage === 1}
                    className={`px-2 py-1 rounded text-sm border ${
                      invitationsPage === 1 ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-50"
                    }`}
                  >
                    Prev
                  </button>

                  <div className="text-sm px-2">
                    Page {invitationsPage} / {totalInvitationPages}
                  </div>

                  <button
                    onClick={() => setInvitationsPage((p) => Math.min(p + 1, totalInvitationPages))}
                    disabled={invitationsPage === totalInvitationPages}
                    className={`px-2 py-1 rounded text-sm border ${
                      invitationsPage === totalInvitationPages
                        ? "opacity-50 cursor-not-allowed"
                        : "hover:bg-gray-50"
                    }`}
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ===== ROW 2 =====
          Left: Upcoming Events (existing)
          Right: empty placeholder (to maintain half width layout)
      */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
        {/* Upcoming Events (kept intact, moved to left of row2) */}
        <div className="bg-white from-indigo-50 via-white to-indigo-100 rounded-2xl shadow-lg p-2 h-full flex flex-col">
          <h2 className="text-2xl font-bold mb-6 text-black">Upcoming Events</h2>

          {loadingEvents ? (
            <div className="text-gray-500 text-center py-8 flex-1">Loading...</div>
          ) : members.length === 0 ? (
            <div className="text-gray-500 text-center py-8 flex-1">No upcoming events found.</div>
          ) : (
            <>
              <div className="flex-1 flex flex-col">
                <motion.ul
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="space-y-4 flex-1 overflow-y-auto p-2"
                >
                  {paginatedEvents.map((event, i) => {
                    const eventDate = new Date(event.eventDateTime?.value);
                    const today = new Date();
                    const tomorrow = new Date();
                    tomorrow.setDate(today.getDate() + 1);
                    const type = event.eventType?.toUpperCase();
                    // Choose default based on type
                    const defaultImg =
                      type === "BIRTHDAY"
                        ? "/images/birthday-default.png"
                        : type === "ANNIVERSARY"
                        ? "/images/anniversary-default.png"
                        : "/images/other-event-default.png"; // for OTHER
                    const invalidPlaceholders = [
                            "default-avatar",
                            "default-birthday",
                            "default-anniversary",
                          ];

                    // Determine if the imageUrl is valid
                    const isInvalid =
                      !event.imageUrl ||
                      invalidPlaceholders.some((p) =>
                        event.imageUrl.toLowerCase().includes(p)
                      );
                    const finalImage = isInvalid ? defaultImg : event.imageUrl;
                    const isToday = eventDate.toDateString() === today.toDateString();
                    const isTomorrow = eventDate.toDateString() === tomorrow.toDateString();

                    return (
                      <motion.li
                        key={event.id || i}
                        whileHover={{ scale: 1.02 }}
                        className="flex items-center gap-4 bg-white border border-indigo-100 rounded-xl shadow-sm p-4 hover:shadow-md transition-all duration-300"
                      >
                        {event.imageUrl ? (
                          <img
                            src={finalImage}
                            alt={event.title}
                            className="w-14 h-14 rounded-lg object-cover border border-indigo-200 object-center"
                          />
                        ) : (
                          <div className="w-14 h-14 rounded-lg bg-indigo-100 flex items-center justify-center text-indigo-600 font-semibold text-lg">
                            {event.eventType?.[0] || "E"}
                          </div>
                        )}

                        <div className="flex-1">
                          <h3 className="text-base font-semibold text-gray-800">{event.title}</h3>
                          <div className="flex items-center gap-1 text-sm text-gray-500">
                          <span>{formatDate(event.eventDateTime)}</span>
                            <span>|</span>
                            <span
                              className={`inline-block mt-2 text-xs font-medium px-2 py-0.5 rounded-full ${
                                type === "BIRTHDAY"
                                  ? "bg-green-100 text-green-700"
                                  : "bg-blue-100 text-blue-700"
                              }`}
                            >
                              {type || "Event"}
                            </span>
                            {isToday && (
                              <span className="bg-green-100 text-green-700 text-xs font-semibold px-2 py-0.5 rounded-full">
                                🎉 Today
                              </span>
                            )}
                            {isTomorrow && (
                              <span className="bg-yellow-100 text-yellow-700 text-xs font-semibold px-2 py-0.5 rounded-full">
                                ⏰ Tomorrow
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-600 mt-1 line-clamp-1">
                            {event.description || "No description available."}
                          </p>
                        </div>

                        <button
                          onClick={() => setSelectedEvent(event)}
                          className="text-indigo-600 border border-indigo-600 hover:bg-indigo-600 hover:text-white text-sm px-3 py-1.5 rounded-lg transition-all"
                        >
                          View Details
                        </button>
                      </motion.li>
                    );
                  })}
                </motion.ul>

                {/* Pagination controls */}
                {totalEventPages > 1 && (
                  <div className="flex justify-center items-center mt-4 gap-2">
                    <button
                      onClick={() => setEventPage((p) => Math.max(p - 1, 1))}
                      disabled={eventPage === 1}
                      className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition-all ${
                        eventPage === 1
                          ? "opacity-50 cursor-not-allowed border-gray-300 text-gray-400"
                          : "border-purple-500 text-purple-600 hover:bg-purple-600 hover:text-white"
                      }`}
                    >
                      ←
                    </button>

                    {Array.from({ length: totalEventPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => setEventPage(page)}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                          eventPage === page ? "bg-purple-600 text-white shadow" : "border border-purple-500 text-purple-600 hover:bg-purple-600 hover:text-white"
                        }`}
                      >
                        {page}
                      </button>
                    ))}

                    <button
                      onClick={() => setEventPage((p) => Math.min(p + 1, totalEventPages))}
                      disabled={eventPage === totalEventPages}
                      className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition-all ${
                        eventPage === totalEventPages ? "opacity-50 cursor-not-allowed border-gray-300 text-gray-400" : "border-purple-500 text-purple-600 hover:bg-purple-600 hover:text-white"
                      }`}
                    >
                      →
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* RIGHT: Empty placeholder to keep half-width layout (you can later place something here) */}
        <div className="bg-transparent" />
      </div>

        {selectedEvent && (() => {
          const type = selectedEvent.eventType?.toUpperCase();

          const defaultImg =
            type === "BIRTHDAY"
              ? "/images/birthday-default.png"
              : type === "ANNIVERSARY"
              ? "/images/anniversary-default.png"
              : "/images/other-event-default.png";

          const invalidPlaceholders = [
            "default-avatar",
            "default-birthday",
            "default-anniversary"
          ];

          const isInvalid =
            !selectedEvent.imageUrl ||
            invalidPlaceholders.some((p) =>
              selectedEvent.imageUrl.toLowerCase().includes(p)
            );

          const popupImage = isInvalid ? defaultImg : selectedEvent.imageUrl;

          return (
            <div className="fixed inset-0 z-50 flex items-center justify-center">
              <div
                className="absolute inset-0 bg-black bg-opacity-40"
                onClick={() => setSelectedEvent(null)}
              ></div>

              <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-md relative z-50">
                <button
                  className="absolute top-2 right-2 text-gray-500 hover:text-red-500 text-xl"
                  onClick={() => setSelectedEvent(null)}
                >
                  &times;
                </button>

                <div className="relative w-full h-56 rounded-lg mb-4 overflow-hidden bg-gray-100">
                  {/* Background blurred */}
                  <img
                    src={popupImage}
                    alt={selectedEvent.title}
                    className="absolute inset-0 w-full h-full object-cover blur-lg scale-110 opacity-70"
                  />
                  {/* Foreground clear */}
                  <img
                    src={popupImage}
                    alt={selectedEvent.title}
                    className="relative z-10 w-full h-full object-contain p-2"
                  />
                </div>

                <h3 className="text-xl font-bold text-gray-800 mb-2">
                  {selectedEvent.title}
                </h3>

                <p className="text-sm text-gray-500 mb-1">
                  {formatDate(selectedEvent.eventDateTime)}
                </p>

                <p className="text-sm text-gray-700 mb-3">
                  {selectedEvent.description || "No description provided."}
                </p>

                <p className="text-sm font-medium text-indigo-700">
                  Family: {selectedEvent.familyName || "Unknown Family"}
                </p>
              </div>
            </div>
          );
        })()}


      {/* Create / Edit Tree Popup (unchanged) */}
      {showPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black opacity-30"></div>
          <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-lg relative z-50">
            <button className="absolute top-2 right-2 text-gray-500 hover:text-red-500 text-xl" onClick={() => setShowPopup(false)}>
              &times;
            </button>
            <CreateFamilyTree
              familyId={editingFamilyId}
              onSuccess={(msg) => {
                setToast(msg);
                setShowPopup(false);
                fetchFamilyTrees();
              }}
              onClose={() => setShowPopup(false)}
            />
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 right-6 bg-black text-white px-4 py-2 rounded shadow">
          {toast}
        </div>
      )}
    </div>
  );
}
